"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createContentDeliveryLayerNode = void 0;
const dht_1 = require("@streamr/dht");
const Handshaker_1 = require("./neighbor-discovery/Handshaker");
const NeighborFinder_1 = require("./neighbor-discovery/NeighborFinder");
const NeighborUpdateManager_1 = require("./neighbor-discovery/NeighborUpdateManager");
const ContentDeliveryLayerNode_1 = require("./ContentDeliveryLayerNode");
const NodeList_1 = require("./NodeList");
const Propagation_1 = require("./propagation/Propagation");
const ProxyConnectionRpcLocal_1 = require("./proxy/ProxyConnectionRpcLocal");
const Inspector_1 = require("./inspect/Inspector");
const TemporaryConnectionRpcLocal_1 = require("./temporary-connection/TemporaryConnectionRpcLocal");
const formStreamPartDeliveryServiceId_1 = require("./formStreamPartDeliveryServiceId");
const PlumTreeManager_1 = require("./plumtree/PlumTreeManager");
const createConfigWithDefaults = (options) => {
    const ownNodeId = (0, dht_1.toNodeId)(options.localPeerDescriptor);
    const rpcCommunicator = options.rpcCommunicator ?? new dht_1.ListeningRpcCommunicator((0, formStreamPartDeliveryServiceId_1.formStreamPartContentDeliveryServiceId)(options.streamPartId), options.transport);
    const neighborTargetCount = options.neighborTargetCount ?? ContentDeliveryLayerNode_1.DEFAULT_NEIGHBOR_TARGET_COUNT;
    const maxContactCount = options.maxContactCount ?? ContentDeliveryLayerNode_1.DEFAULT_NODE_VIEW_SIZE;
    const acceptProxyConnections = options.acceptProxyConnections ?? ContentDeliveryLayerNode_1.DEFAULT_ACCEPT_PROXY_CONNECTIONS;
    const neighborUpdateInterval = options.neighborUpdateInterval ?? NeighborUpdateManager_1.DEFAULT_NEIGHBOR_UPDATE_INTERVAL;
    const minPropagationTargets = options.minPropagationTargets ?? Propagation_1.DEFAULT_MIN_PROPAGATION_TARGETS;
    const maxPropagationBufferSize = options.maxPropagationBufferSize ?? Propagation_1.DEFAULT_MAX_PROPAGATION_BUFFER_SIZE;
    const neighbors = options.neighbors ?? new NodeList_1.NodeList(ownNodeId, maxContactCount);
    const leftNodeView = options.leftNodeView ?? new NodeList_1.NodeList(ownNodeId, maxContactCount);
    const rightNodeView = options.rightNodeView ?? new NodeList_1.NodeList(ownNodeId, maxContactCount);
    const nearbyNodeView = options.nearbyNodeView ?? new NodeList_1.NodeList(ownNodeId, maxContactCount);
    const randomNodeView = options.randomNodeView ?? new NodeList_1.NodeList(ownNodeId, maxContactCount);
    const ongoingHandshakes = new Set();
    const temporaryConnectionRpcLocal = new TemporaryConnectionRpcLocal_1.TemporaryConnectionRpcLocal({
        rpcCommunicator,
        localPeerDescriptor: options.localPeerDescriptor,
        streamPartId: options.streamPartId,
        connectionLocker: options.connectionLocker
    });
    const proxyConnectionRpcLocal = acceptProxyConnections ? new ProxyConnectionRpcLocal_1.ProxyConnectionRpcLocal({
        localPeerDescriptor: options.localPeerDescriptor,
        streamPartId: options.streamPartId,
        rpcCommunicator
    }) : undefined;
    const plumTreeManager = options.plumTreeOptimisation ? new PlumTreeManager_1.PlumTreeManager({
        neighbors,
        localPeerDescriptor: options.localPeerDescriptor,
        rpcCommunicator
    }) : undefined;
    const propagation = options.propagation ?? new Propagation_1.Propagation({
        minPropagationTargets,
        maxMessages: maxPropagationBufferSize,
        ttl: Propagation_1.DEFAULT_PROPAGATION_BUFFER_TTL,
        sendToNeighbor: async (neighborId, msg) => {
            const remote = neighbors.get(neighborId) ?? temporaryConnectionRpcLocal.getNodes().get(neighborId);
            const proxyConnection = proxyConnectionRpcLocal?.getConnection(neighborId);
            if (remote) {
                await remote.sendStreamMessage(msg, options.doNotBufferWhileConnecting);
            }
            else if (proxyConnection) {
                await proxyConnection.remote.sendStreamMessage(msg);
            }
            else {
                throw new Error('Propagation target not found');
            }
        }
    });
    const handshaker = options.handshaker ?? new Handshaker_1.Handshaker({
        localPeerDescriptor: options.localPeerDescriptor,
        streamPartId: options.streamPartId,
        rpcCommunicator,
        neighbors,
        leftNodeView,
        rightNodeView,
        nearbyNodeView,
        randomNodeView,
        maxNeighborCount: neighborTargetCount,
        rpcRequestTimeout: options.rpcRequestTimeout,
        ongoingHandshakes
    });
    const neighborFinder = options.neighborFinder ?? new NeighborFinder_1.NeighborFinder({
        neighbors,
        leftNodeView,
        rightNodeView,
        nearbyNodeView,
        randomNodeView,
        doFindNeighbors: (excludedIds) => handshaker.attemptHandshakesOnContacts(excludedIds),
        minCount: neighborTargetCount
    });
    const neighborUpdateManager = options.neighborUpdateManager ?? new NeighborUpdateManager_1.NeighborUpdateManager({
        neighbors,
        nearbyNodeView,
        localPeerDescriptor: options.localPeerDescriptor,
        neighborFinder,
        streamPartId: options.streamPartId,
        rpcCommunicator,
        neighborUpdateInterval,
        neighborTargetCount,
        ongoingHandshakes
    });
    const inspector = options.inspector ?? new Inspector_1.Inspector({
        localPeerDescriptor: options.localPeerDescriptor,
        rpcCommunicator,
        streamPartId: options.streamPartId,
        connectionLocker: options.connectionLocker
    });
    return {
        ...options,
        neighbors,
        leftNodeView,
        rightNodeView,
        nearbyNodeView,
        randomNodeView,
        rpcCommunicator,
        handshaker,
        neighborFinder,
        neighborUpdateManager,
        propagation,
        neighborTargetCount,
        nodeViewSize: maxContactCount,
        proxyConnectionRpcLocal,
        inspector,
        temporaryConnectionRpcLocal,
        plumTreeManager
    };
};
const createContentDeliveryLayerNode = (options) => {
    return new ContentDeliveryLayerNode_1.ContentDeliveryLayerNode(createConfigWithDefaults(options));
};
exports.createContentDeliveryLayerNode = createContentDeliveryLayerNode;
//# sourceMappingURL=createContentDeliveryLayerNode.js.map